"use client";

import { motion } from "framer-motion";
import { useMemo } from "react";

/**
 * TextReveal — splits the provided text into words and reveals each word
 * on scroll (mask + y translate), staggered. Same feel as the About/Team
 * name reveal, upgraded to word-level granularity.
 *
 * Usage:
 *   <TextReveal as="h2" className="font-serif text-6xl">
 *     Some heading text
 *   </TextReveal>
 *
 * Accepts an optional `children` string (or ReactNode). If children is a
 * ReactNode with mixed inline formatting, prefer passing a plain string.
 */
export const TextReveal = ({
  as: Tag = "span",
  children,
  className = "",
  delay = 0,
  stagger = 0.07,
  duration = 0.95,
  y = "115%",
  ease = [0.22, 1, 0.36, 1],
  once = false,
  amount = 0.4,
  ...rest
}) => {
  const words = useMemo(() => {
    if (typeof children === "string") return children.split(/(\s+)/);
    // If ReactNode is passed, fall back to plain animate wrap
    return null;
  }, [children]);

  const container = {
    hidden: {},
    show: {
      transition: {
        delayChildren: delay,
        staggerChildren: stagger,
      },
    },
  };

  const word = {
    hidden: { y },
    show: {
      y: "0%",
      transition: { duration, ease },
    },
  };

  if (!words) {
    // Non-string children — animate the whole block once
    return (
      <motion.span
        className={className}
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once, amount }}
        transition={{ duration, ease, delay }}
      >
        {children}
      </motion.span>
    );
  }

  return (
    <Tag className={className} {...rest}>
      <motion.span
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once, amount }}
        className="inline"
      >
        {words.map((w, i) =>
          /^\s+$/.test(w) ? (
            <span key={`s-${i}`}> </span>
          ) : (
            <span
              key={`w-${i}`}
              className="inline-block overflow-hidden align-bottom pb-[0.1em] leading-[1.1]"
            >
              <motion.span
                variants={word}
                className="inline-block"
                style={{ willChange: "transform" }}
              >
                {w}
              </motion.span>
            </span>
          ),
        )}
      </motion.span>
    </Tag>
  );
};

export default TextReveal;
